import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.jpeg"; // Adjust the path as necessary
import { FiChevronDown, FiChevronRight, FiLogOut } from "react-icons/fi";

/**
 * Sidebar Component
 *
 * Props:
 * - menuItems: Array of menu item objects:
 * [
 * {
 * label: string,      // The text to display
 * icon: ReactNode,    // The icon component (e.g., <FaUsers />)
 * path?: string,      // Optional route path for <Link>
 * children?: Array<{  // Optional array for sub-menu items
 * label: string,
 * icon: ReactNode,
 * path: string
 * }>
 * },
 * ...
 * ]
 * - onLogout: function - Callback when logout button is clicked
 */
const Sidebar = ({ menuItems = [], onLogout = () => {} }) => {
  const location = useLocation();
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Function to toggle the state of a dropdown
  const toggleDropdown = (label) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Effect to ensure parent dropdowns are open if a child is the current path
  useEffect(() => {
    const newOpenDropdowns = {};
    menuItems.forEach((item) => {
      if (item.children) {
        const isActiveChild = item.children.some(
          (child) => location.pathname === child.path
        );
        if (isActiveChild) {
          newOpenDropdowns[item.label] = true;
        }
      }
    });
    setOpenDropdowns((prev) => ({ ...prev, ...newOpenDropdowns }));
  }, [location.pathname, menuItems]);

  const renderMenuItem = (item, index, level = 0) => {
    const isActive = item.path && location.pathname === item.path;
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openDropdowns[item.label] || false;

    const baseClasses = `flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer`;
    const activeClasses = `bg-orange-100 text-orange-600 font-semibold`;
    const inactiveClasses = `text-gray-700 hover:bg-gray-100 hover:text-orange-500`;
    const itemClasses = isActive ? activeClasses : inactiveClasses;
    const paddingLeft = level > 0 ? `pl-${4 + level * 2}` : `pl-3`;

    const content = (
      <div className={`${baseClasses} ${itemClasses} ${paddingLeft}`}>
        {item.icon}
        <span className="flex-1">{item.label}</span>
        {hasChildren && (
          <span className="transition-transform duration-200">
            {isOpen ? <FiChevronDown /> : <FiChevronRight />}
          </span>
        )}
      </div>
    );

    return (
      <li key={index}>
        {item.path ? (
          <Link to={item.path} className="block">
            {content}
          </Link>
        ) : (
          <div onClick={() => hasChildren && toggleDropdown(item.label)}>
            {content}
          </div>
        )}

        {hasChildren && isOpen && (
          <ul className="pl-6 space-y-1 mt-1 transition-all duration-300 ease-in-out">
            {item.children.map((child, childIndex) =>
              renderMenuItem(child, `${index}-${childIndex}`, level + 1)
            )}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="fixed left-0 top-0 w-64 h-screen bg-white shadow-md flex flex-col justify-between z-50">
      <div>
        <div className="flex items-center justify-center py-6">
          <img src={logo} alt="Hold the child logo" className="w-28" />
        </div>

        <ul className="space-y-1 px-4">
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </ul>
      </div>

      <div className="px-4 py-4">
        <button
          onClick={onLogout}
          className="flex items-center w-full gap-3 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-all duration-200"
        >
          <FiLogOut className="h-5 w-5" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;